import { useState } from 'react';
import { Box } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import DDMNavBar from '@/externals/ddm/components/DDMNavBar';
import { DialogContainer } from '@/modules/MuiDialog';
import { useAutomationLimit } from '@/hooks/queries/dm-automation';
import type { DmAutomationDto } from '@/types/dmAutomation';
import AutoDmSidebar, { type DmSection } from './Sidebar';
import AutoDmRoot, { type AutoDmView } from '@/pages/AutoDm';

const AutoDmLayout = () => {
    const [appBarHeight, setAppBarHeight] = useState(64);
    const [section, setSection] = useState<DmSection>('list');
    const [view, setView] = useState<AutoDmView>('list');
    const [editing, setEditing] = useState<DmAutomationDto | null>(null);

    // 남은 개수(remaining) — 경량 /automations/limit 로 조회. null(미로드)이면 게이팅하지 않음(서버 400 최종 방어).
    const limitQuery = useAutomationLimit();
    const remaining = limitQuery.data?.remaining ?? null;
    // 오토DM 인증 잠금(미인증) — autodmUnlocked === false 일 때만 잠금(미로드/미제공은 잠그지 않음)
    const autodmLocked = limitQuery.data?.autodmUnlocked === false;

    const handleChangeSection = (next: DmSection) => {
        setSection(next);
        setView('list');
        setEditing(null);
    };

    const handleCreate = () => {
        // 미인증이면 인증 화면으로 (list 섹션에서 게이트가 인증 화면 노출)
        if (autodmLocked) {
            setSection('list');
            setView('list');
            setEditing(null);
            return;
        }
        if (remaining !== null && remaining <= 0) {
            toast.warn('이용권을 모두 사용했어요. 충전하면 자동화를 더 만들 수 있어요.');
            setSection('pass');
            setView('list');
            setEditing(null);
            return;
        }
        setSection('list');
        setView('wizard');
        setEditing(null);
    };

    const handleStartEdit = (automation: DmAutomationDto) => {
        setSection('list');
        setEditing(automation);
        setView('wizard');
    };

    const handleExitWizard = () => {
        setView('list');
        setEditing(null);
    };

    return (
        <>
            <Helmet>
                <title>자동DM · FLOWING SPACE</title>
            </Helmet>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh',
                    '--app-bar-height': `${appBarHeight}px`,
                }}
            >
                <DDMNavBar setAppBarHeight={setAppBarHeight} />

                <Box
                    sx={{
                        display: 'flex',
                        flexGrow: 1,
                        minHeight: `calc(100vh - ${appBarHeight}px)`,
                        bgcolor: '#f4f5f7',
                    }}
                >
                    <AutoDmSidebar active={section} onChange={handleChangeSection} onCreate={handleCreate} />

                    <Box
                        component="main"
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            minWidth: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <AutoDmRoot
                            section={section}
                            view={view}
                            editing={editing}
                            onChangeView={setView}
                            onChangeSection={setSection}
                            onStartEdit={handleStartEdit}
                            onExitWizard={handleExitWizard}
                        />
                    </Box>
                </Box>
            </Box>

            <DialogContainer />
        </>
    );
};

export default AutoDmLayout;
